import turf from "turf";
import proj4 from 'proj4';
import "../../Events";
import "GIS/Map/Layer/Base";
import { InvalidArgumentError } from '../../errors';

/** @namespace GIS.Map */
NS("GIS.Map");

const STYLE_NAME_PROP = 'styleName'
const PREV_STYLE_NAME_PROP = 'prevStyleName'

/**
 * @class
 * @extends Events
 */
GIS.Map.Base = Class(Events, /** @lends GIS.Map.Base.prototype */{
    STYLE_NAME_PROP: STYLE_NAME_PROP,
    PREV_STYLE_NAME_PROP: PREV_STYLE_NAME_PROP,
    // options
    projection: MAP_PROJ,
    displayProjection: DISPLAY_PROJ,
    center: [0, 0],
    zoom: 1,
    // basic constants, remove later
    /** @type {string} */
    vendor: "unknown",
    /**
     * @protected
     * @type {Object.<string, GIS.Map.Layer.Base>}
     */
    layers: {},
    /**
     * @param {string|Element|jQuery.<Element>} container
     * @param {Object} [options]
     */
    initialize(container, options) {
        options || (options = {});
        _.merge(this, options);

        this.prepareOptions(options);
        this.createMap(container, options);
    },
    /**
     * @param {Object} options
     */
    prepareOptions(options) { },
    /**
     * @fires GIS.Map.Base#before.destroy
     * @fires GIS.Map.Base#destroy
     * @returns {GIS.Map.Base}
     */
    destroy() {
        this.emit("before.destroy");

        Util.each(this.layers, layer => layer.destroy());

        this.emit("destroy").off();

        return this;
    },
    /**
     * @param container
     * @param options
     * @protected
     */
    createMap(container, options) {
        throw new NotImplementedError("abstract method `createMap` isn't implemented");
    },
    /**
     * Updates map viewport size
     * @fires GIS.Map.Base#align
     * @returns {GIS.Map.Base}
     */
    align() {
        /**
         * @vent GIS.Map.Base#align
         */
        this.emit("align");

        return this;
    },
    /**
     * Redraws map
     * @fires GIS.Map.Base#refresh
     * @returns {GIS.Map.Base}
     */
    refresh() {
        /**
         * @event @vent GIS.Map.Base#refresh
         */
        this.emit("refresh");

        return this;
    },
    /**
     * @param name
     * @param {GIS.Map.Layer.Base|string} layerClass
     * @param {Object} [options]
     * @returns {GIS.Map.Layer.Base}
     * @throws RuntimeError
     * @fires GIS.Map.Base#add.layer
     */
    addLayer(name, layerClass, options) {
        var layer;

        if (Util.isString(layerClass)) {
            layer = Util.create(layerClass, [this, options]);
        } else if (Util.isFunction(layerClass)) {
            layer = new layerClass(this, options);
        } else {
            layer = layerClass;
        }

        if (!Class.Is(layer, GIS.Map.Layer.Base)) {
            throw new RuntimeError("Wrong layer argument");
        }

        this.layers[name] = layer;

        layer.on("destroy", () => {
            delete this.layers[name];
            this.emit("remove.layer", { layer });
        });

        this.emit("add.layer", { layer });

        return layer;
    },
    /**
     * @param name
     * @returns {GIS.Map.Base}
     * @fires GIS.Map.Base#remove.layer
     */
    removeLayer(name) {
        if (this.layers[name]) {
            this.layers[name].destroy();
        }

        return this;
    },
    /**
     * @param name
     * @returns {GIS.Map.Layer.Base|undefined}
     */
    getLayer(name) {
        return this.layers[name];
    },
    /**
     * @returns {Array.<GIS.Map.Layer.Base>}
     */
    getLayers() {
        return _.values(this.layers);
    },
    /**
     * @param {Function|Object|string} predicate
     * @param {*} [context]
     * @returns {GIS.Map.Layer.Base|undefined}
     */
    findLayer(predicate, context) {
        return _.find(this.layers, predicate, context || this);
    },
    /**
     * @param {Object} geog
     * @param {number} [zoom]
     */
    setCenter(geog, zoom) {},
    /**
     * @returns {Array.<number>|undefined}
     */
    getCenter() {},
    /**
     * @param {number} zoom
     */
    setZoom(zoom) {},
    /**
     * @returns {number|undefined}
     */
    getZoom() {},
    /**
     * Sorts layers on map
     * @param {function(a : GIS.Map.Layer.Base, b : GIS.Map.Layer.Base)} predicate
     * @param {*} [context]
     * @returns {GIS.Map.Base}
     */
    sortLayers(predicate, context) {
        _.sortBy(this.layers, predicate, context || this);

        return this;
    },
    /**
     * @param {number} delta
     * @returns {*}
     */
    zoomByDelta(delta) {},
    /**
     * @returns {*}
     */
    zoomIn() {},
    /**
     * @returns {*}
     */
    zoomOut() {},
    /**
     * @returns {*} Vendor specific object
     */
    getVendor() {},
    /**
     * @returns {number[]|undefined}
     */
    getCurrentPosition() {},
    /**
     * @returns {string}
     */
    getProjection()
    {
        return this.projection;
    },
    /**
     * @param {number[]} geom
     * @param {number[]} [size]
     * @param {*} [options]
     * @returns {*}
     */
    fit(geom, size, options = {}) { },
    /**
     * @param {Object|string} geoJson
     * @param {string} [geometryProjection]
     * @param {string} [geoJSONProjection]
     * @returns {*}
     */
    geomFromGeoJSON(geoJson, geometryProjection = this.getProjection(), geoJSONProjection = this.displayProjection) {},
    /**
     * @returns {glStyleHash}
     */
    getDefaultStyleHash,
    /**
     * @param {glStyleHash} style
     * @returns {glStyleHash}
     */
    getStyle,
    /**
     * Defines custom projection.
     * @param {string|Array} code
     * @param {string} [def]
     * @param {Object} [options]
     * @param {function} [callback]
     */
    defineProjection,
    /**
     * @param {number[]} coordinate
     * @param {string} source Source projection code
     * @param {string} destination Destination projection code
     * @returns {number[]}
     */
    transformCoordinateProjection,
    /**
     * @param {number[]}extent
     * @param {string} source
     * @param {string} destination
     * @returns {number[]}
     */
    transformExtentProjection,
    /**
     * @param {Object} geom
     * @returns {number[]}
     */
    pointOnSurface,
    /**
     * @param {number[]} coordinates
     * @param {string|number} type
     * @returns {{type: string, coordinates: Array}}
     */
    toGeoJSON
});

GIS.Map.Base.Static(/** @lends GIS.Map.Base */{
    STYLE_NAME_PROP: STYLE_NAME_PROP,
    PREV_STYLE_NAME_PROP: PREV_STYLE_NAME_PROP,
    /** @type {string[]} */
    definedProjections: [ DISPLAY_PROJ, MAP_PROJ ],
    /**
     * @returns {glStyleHash}
     */
    getDefaultStyleHash,
    /**
     * @param {glStyleHash} style
     * @returns {glStyleHash}
     */
    getStyle,
    /**
     * Defines custom projection.
     * @param {string|Array} code
     * @param {string} [def]
     * @param {Object} [options]
     * @param {function} [callback]
     */
    defineProjection,
    /**
     * @param {number[]} coordinate
     * @param {string} source Source projection code
     * @param {string} destination Destination projection code
     * @returns {number[]}
     */
    transformCoordinateProjection,
    /**
     * @param {number[]}extent
     * @param {string} source
     * @param {string} destination
     * @returns {number[]}
     */
    transformExtentProjection,
    /**
     * @param {Object} geom
     * @returns {number[]}
     */
    pointOnSurface,
    /**
     * @param {number[]} coordinates
     * @param {string|number} type
     * @returns {{type: string, coordinates: Array}}
     */
    toGeoJSON
});


/**
 * Defines custom projection.
 * @param {string|Array} code
 * @param {string} [def]
 * @param {Object} [options]
 * @param {function} [callback]
 * @static
 * @public
 */
function defineProjection(code, def, options, callback) {
    var definitions = [];

    if (_.isArray(code)) {
        definitions = code;
    } else if (_.isString(code)) {
        definitions.push({
            code,
            options,
            definition: def,
        });
    }

    _.chain(definitions)
        .filter((def) => !_.includes(GIS.Map.Base.definedProjections, def.code.toUpperCase()))
        .forEach((projDef) => {
            proj4.defs(projDef.code, projDef.definition);
            GIS.Map.Base.definedProjections.push(projDef.code);

            Util.call(callback, null, [projDef]);
        })
        .value();
}

/**
 * @param {number[]} coordinate
 * @param {string} source Source projection code
 * @param {string} destination Destination projection code
 * @returns {number[]}
 * @static
 * @public
 */
function transformCoordinateProjection(coordinate, source, destination) {
    return proj4(source, destination, coordinate);
}

/**
 * @param {number[]}extent
 * @param {string} source
 * @param {string} destination
 * @returns {number[]}
 * @static
 * @public
 */
function transformExtentProjection(extent, source, destination) {
    return proj4(source, destination, extent.slice(0, 2)).concat(proj4(source, destination, extent.slice(2)));
}

/**
 * @returns {glStyleHash}
 * @static
 * @public
 *
 * todo под конкретный тип фечера добавлять отдельный Набор стилей
 */
function getDefaultStyleHash()
{
    const default_ = {
        fillColor: [255, 255, 255, 0.7],
        strokeColor: [30, 54, 133, 1],
        strokeWidth: 3,
        strokeCap: "round",
        strokeJoin: "round",
        iconRadius: 7,
        textFillColor: [255, 255, 255, 1],
        textStrokeColor: [30, 54, 133, 1],
        textFont: "Helvetica, Verdana, sans-serif",
        textFontSize: 14,
        textStrokeWidth: 4,
        textOverflow: true
    };

    const select = _.assign({}, default_, {
        fillColor: [255, 255, 255, 0.8],
        strokeColor: [255, 121, 1, 1],
        textFillColor: [255, 121, 1, 1],
        textStrokeColor: [255, 121, 1, 1],
        zIndex: 1
    });

    const cluster = _.assign({}, default_, {
        title: "<%= item.clusterSize %>",
        iconUrl: null,
        iconImg: null,
        iconPoints: null,
        iconRadius: 20,
        textFontSize: 14,
        zIndex: 1
    });

    const modify = _.assign({}, default_, {
        fillColor: [255, 255, 255, 0.8],
        strokeColor: "#FF1E23",
        zIndex: 1
    });

    const current = _.assign({}, default_, {
        fillColor: [27, 226, 23, 0.8],
        strokeColor: [14, 118, 11, 1],
        strokeWidth: 4,
        zIndex: 1
    });

    const feature = _.assign({}, default_, {
        fillColor: "<%= (item.style?item.style.fillColor:0) %>",
        strokeColor: "<%= (item.style?item.style.strokeColor:0) %>"
    });

    return {
        "default": {
            styles: [_.cloneDeep(default_)]
        },
        select: {
            styles: [_.cloneDeep(select)]
        },
        cluster: {
            styles: [_.cloneDeep(cluster)]
        },
        remove: null,
        modify: {
            styles: [_.cloneDeep(modify)]
        },
        current: {
            styles: [_.cloneDeep(current)]
        },
        feature: {
            styles: [_.cloneDeep(feature)]
        }
    };
}

/**
 * Returns glStyleHash merged with default styles
 * @param {glStyleHash} styleHash
 * @returns {glStyleHash}
 * @static
 * @public
 */
function getStyle(styleHash)
{
    return _.merge(getDefaultStyleHash(), styleHash);
}

/**
 * @param {Object} geom
 * @returns {number[]}
 * @static
 * @public
 */
function pointOnSurface(geom) {
    return turf.pointOnSurface(geom).geometry.coordinates;
}

/**
 * @param {number[]} coordinates
 * @param {string|number} type
 * @returns {{type: string, coordinates: Array}}
 * @static
 * @public
 */
function toGeoJSON(coordinates, type) {
    assert(_.isArray(coordinates), "`coordinates` must be of type Array", InvalidArgumentError);
    assert(!!type, "`type` cann't be empty", InvalidArgumentError);

    return {
        "type": _.isNumber(type) ? GIS.Geometry.name(type) : type,
        "coordinates": coordinates
    };
}
